import { formatHistoryDate } from 'services/travProAgentStream';

import type { ConversationEntry, PersistedThread } from './types';

const STORAGE_KEY = 'smartAssist_conversations';

const threadStorageKey = (appId: string): string => {
  return `smartAssist_thread_${appId}`;
};

export const readPersistedThread = (appId: string): PersistedThread | null => {
  if (!appId) return null;
  try {
    const raw = sessionStorage.getItem(threadStorageKey(appId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'conversationId' in parsed &&
      'appId' in parsed &&
      String((parsed as PersistedThread).appId) === String(appId)
    ) {
      return parsed as PersistedThread;
    }
  } catch {
    /* ignore corrupt storage */
  }
  return null;
};

export const savePersistedThread = (appId: string, conversationId: string): void => {
  if (!appId || !conversationId) return;
  sessionStorage.setItem(
    threadStorageKey(appId),
    JSON.stringify({ appId: String(appId), conversationId: String(conversationId) }),
  );
};

export const clearPersistedThread = (appId: string): void => {
  if (!appId) return;
  sessionStorage.removeItem(threadStorageKey(appId));
};

export const getConversations = (): ConversationEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConversationEntry[]) : [];
  } catch {
    return [];
  }
};

export const saveConversation = (query: string): ConversationEntry[] => {
  const convos = getConversations();
  const entry: ConversationEntry = { id: Date.now(), text: query, date: formatHistoryDate(Date.now()) };
  const updated = [
    entry,
    ...convos.filter((c) => {
      return c.text !== query;
    }),
  ].slice(0, 10);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
};

export const removeLocalConversation = (conversationId: string): ConversationEntry[] => {
  const convos = getConversations();
  const updated = convos.filter((c) => {
    return String(c.conversationId) !== String(conversationId);
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
};

export const mergeConversationIdForFirstMessage = (
  messageText: string,
  conversationId: string,
): ConversationEntry[] | null => {
  const convos = getConversations();
  const idx = convos.findIndex((c) => {
    return c.text === messageText && !c.conversationId;
  });
  if (idx === -1) return null;
  const current = convos[idx];
  if (!current) return null;
  const next = [...convos];
  next[idx] = { ...current, conversationId: String(conversationId) };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
};
